import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useNotify } from "@/hooks/useNotify";
import ProfilePhotoUpload from "@/components/ui/profile-photo-upload";
import PasswordChangeForm from "@/components/settings/PasswordChangeForm";
import SignaturePreview from "@/components/settings/signature-builder/SignaturePreview";
import { EmailAssetsUploader } from "@/components/settings/EmailAssetsUploader";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useImpersonationGuard } from "@/hooks/useImpersonationGuard";
import { useQuery } from "@tanstack/react-query";
import PageLayout from "@/components/layout/PageLayout";
import PageHeader from "@/components/layout/PageHeader";
import { toast } from "sonner";

const ProfileSettings = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyRole, setCompanyRole] = useState("");
  const [personalPhoneNumber, setPersonalPhoneNumber] = useState("");
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailChangePassword, setEmailChangePassword] = useState('');
  const [showEmailChangePassword, setShowEmailChangePassword] = useState(false);

  const notify = useNotify();
  const { user, profile } = useAuth();
  const { guardWrite } = useImpersonationGuard();

  // Fetch company data for signature preview
  const { data: company } = useQuery({
    queryKey: ['company-for-signature', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return null;
      const { data, error } = await supabase
        .from('companies')
        .select('name, email, company_phone_number, website')
        .eq('id', profile.company_id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.company_id,
  });

  const fetchProfileData = async () => {
    if (!user?.id) return;

    try {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, last_name, company_position, personal_phone_number, profile_photo_url, email')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      if (profileData) {
        setFirstName(profileData.first_name || "");
        setLastName(profileData.last_name || "");
        setCompanyRole(profileData.company_position || "");
        setPersonalPhoneNumber(profileData.personal_phone_number || "");
        setProfilePhotoUrl(profileData.profile_photo_url || null);
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
      notify.error("Failed to load profile data");
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, [user?.id]);

  useEffect(() => {
    if (profile?.profile_photo_url && !profilePhotoUrl) {
      setProfilePhotoUrl(profile.profile_photo_url);
    }
  }, [profile?.profile_photo_url, profilePhotoUrl]);

  const handleSavePersonal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guardWrite('edit your profile')) return;
    
    if (!user?.id) {
      notify.error("You must be logged in to update your profile.");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: firstName,
          last_name: lastName,
          company_position: companyRole,
          personal_phone_number: personalPhoneNumber,
          profile_photo_url: profilePhotoUrl,
          updated_at: new Date().toISOString()
        })
        .eq("id", user.id);
      
      if (error) throw error;
      
      notify.success("Your personal information has been updated successfully.");
    } catch (error: any) {
      console.error('Personal update failed:', error);
      notify.error(error.message || "An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);

    const currentEmail = user?.email ?? '';
    if (!newEmail.trim()) {
      setEmailError('Please enter a new email address.');
      return;
    }
    if (newEmail.toLowerCase() === currentEmail.toLowerCase()) {
      setEmailError('New email must be different from your current email.');
      return;
    }

    if (!emailChangePassword) {
      setEmailError('Please enter your current password.');
      return;
    }

    setEmailSubmitting(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: currentEmail,
        password: emailChangePassword,
      });
      if (signInError) {
        setEmailError('Current password is incorrect.');
        setEmailSubmitting(false);
        return;
      }

      const { error } = await supabase.auth.updateUser({
        email: newEmail.trim(),
        options: {
          emailRedirectTo: `${window.location.origin}/email-change-success`
        }
      } as any);
      if (error) throw error;
      setEmailSent(true);
      toast.success('Confirmation email sent to your new address. Click the link in that email to complete the change.');
      setNewEmail('');
      setEmailChangePassword('');
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Failed to send confirmation email.');
    } finally {
      setEmailSubmitting(false);
    }
  };
  return (
    <PageLayout maxWidth="md">
      <PageHeader 
        title="Profile Settings"
        description="Manage your personal information and security"
      />

      <Card>
        <form onSubmit={handleSavePersonal}>
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
            <CardDescription>
              Update your personal details and profile photo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex justify-center mb-4">
              <ProfilePhotoUpload 
                key={profilePhotoUrl}
                onImageUpload={setProfilePhotoUrl}
                initialImage={profilePhotoUrl || undefined}
                userId={user?.id}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="companyRole">Your Position/Role</Label>
              <Input
                id="companyRole"
                placeholder="e.g. Owner, Project Manager, Estimator"
                value={companyRole}
                onChange={(e) => setCompanyRole(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="personalPhoneNumber">Personal Phone Number</Label>
              <Input
                id="personalPhoneNumber"
                type="tel"
                value={personalPhoneNumber}
                onChange={(e) => setPersonalPhoneNumber(e.target.value)}
              />
            </div>
          </CardContent>
          <CardFooter>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </CardFooter>
        </form>
      </Card>

      {/* Change Email Card */}
      <Card className="mt-6">
        <form onSubmit={handleEmailChange}>
          <CardHeader>
            <CardTitle>Change Email</CardTitle>
            <CardDescription>
              A confirmation link will be sent to your new address. Your email only changes after you confirm it.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentEmail">Current Email</Label>
              <Input id="currentEmail" type="email" value={user?.email ?? ''} readOnly disabled />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newEmail">New Email</Label>
              <Input
                id="newEmail"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emailChangePassword">Current Password</Label>
              <div className="relative">
                <Input
                  id="emailChangePassword"
                  type={showEmailChangePassword ? 'text' : 'password'}
                  value={emailChangePassword}
                  onChange={(e) => setEmailChangePassword(e.target.value)}
                  placeholder="Enter your current password"
                  className="pr-20"
                />
                <button
                  type="button"
                  onClick={() => setShowEmailChangePassword((v) => !v)}
                  className="absolute inset-y-0 right-0 px-3 text-sm text-muted-foreground hover:text-foreground"
                  aria-label={showEmailChangePassword ? 'Hide password' : 'Show password'}
                >
                  {showEmailChangePassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            {emailError && (
              <p className="text-sm text-destructive">{emailError}</p>
            )}
            {emailSent && (
              <p className="text-sm text-muted-foreground">
                A confirmation link has been sent to your new email address. Click the link in that email to complete the change.
              </p>
            )}
          </CardContent>
          <CardFooter>
            <Button type="submit" disabled={emailSubmitting}>
              {emailSubmitting ? 'Sending...' : 'Send confirmation email'}
            </Button>
          </CardFooter>
        </form>
      </Card>

      {/* Email Signature Card */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Email Signature</CardTitle>
          <CardDescription>
            Your email signature is generated from your company's template combined with your personal information above.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SignaturePreview
            userProfile={{
              first_name: firstName,
              last_name: lastName,
              company_position: companyRole,
              personal_phone_number: personalPhoneNumber,
              email: user?.email,
              profile_photo_url: profilePhotoUrl || undefined,
            }}
            company={company || undefined}
          />
        </CardContent>
      </Card>

      {/* Email Assets Uploader */}
      <div className="mt-6">
        <EmailAssetsUploader />
      </div>
      
      <PasswordChangeForm />
    </PageLayout>
  );
};

export default ProfileSettings;
